import {
  ChakraFlex,
  EmptySectionCard,
  FAIconName,
  ModalV2,
  SkeletonLoader,
  TextInputV2,
  Text,
  Avatar,
  Tag,
  TagColor,
} from '@equip.health/ui';
import { useMemo, useState } from 'react';
import { capitalizeFirstLetter } from '~/lib/common';
import { getPreferredFullName } from '~/lib/util/patient.util';
import { useGetRegistrantsQuery } from '~/store/services/groupClass';
import { capitalizeEachWord } from '../util';
import { getFilteredRegistrants } from '~/lib/util/groups.utils';

export interface ViewRegistrantsListProps {
  id: string;
  isOpen: boolean;
  onViewRegistrantsListClose: () => void;
}

const TOP_LEVEL_ID = 'view-registrants-list';
const MAX_VISIBLE_REGISTRANTS = 100;

const ViewRegistrantsModal = ({
  id,
  isOpen,
  onViewRegistrantsListClose,
}: ViewRegistrantsListProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { data: registrants, isLoading: isRegistrantsLoading } =
    useGetRegistrantsQuery(id, { skip: !isOpen });

  const filteredRegistrants = useMemo(
    () => getFilteredRegistrants(searchTerm, registrants),
    [searchTerm, registrants],
  );

  const visibleRegistrants = useMemo(
    () => filteredRegistrants?.slice(0, MAX_VISIBLE_REGISTRANTS) || [],
    [filteredRegistrants],
  );

  const noOfRegistrants = visibleRegistrants.length;

  const renderRegistrants = useMemo(
    () =>
      visibleRegistrants.map((registrant) => {
        const label = getPreferredFullName(
          registrant.chosenName,
          registrant.firstName,
          registrant.lastName,
          false,
        );

        const fullName = capitalizeEachWord(label);

        const registrantType = capitalizeFirstLetter(registrant.type);

        return (
          <ChakraFlex
            flexDirection="row"
            alignItems="flex-start"
            alignSelf="stretch"
            gap="12px"
            key={registrant.externalId}
          >
            <Avatar
              id={`${TOP_LEVEL_ID}__avatar-${registrant.externalId}`}
              name={fullName}
              size="sm"
              userType="patient"
            />
            <ChakraFlex
              flexDirection="column"
              alignItems="flex-start"
              flex="1 0 0"
              gap="2px"
            >
              <Text variant="bodyMedium">{fullName}</Text>
              <Text color="neutral.secondary" variant="caption">
                {registrantType}
              </Text>
            </ChakraFlex>
            {registrant?.isDeclined && (
              <Tag
                color={TagColor.gray}
                id={`${TOP_LEVEL_ID}__declined-tag-${registrant.externalId}`}
                size="sm"
                value="Declined"
              />
            )}
          </ChakraFlex>
        );
      }),
    [visibleRegistrants],
  );

  return (
    <ModalV2
      isOpen={isOpen}
      onClose={onViewRegistrantsListClose}
      title="View registrants"
      size="md"
      id={`${TOP_LEVEL_ID}__modal`}
      height="640px"
    >
      <ChakraFlex flexDirection="column" alignItems="flex-start" gap="24px">
        <TextInputV2
          id={`${TOP_LEVEL_ID}__search-registrants`}
          leftIcon={FAIconName.searchRegular}
          onChange={(value) => {
            setSearchTerm(value);
          }}
          placeholder="Search registrants"
          value={searchTerm}
        />
        <SkeletonLoader
          id={`${TOP_LEVEL_ID}__skeleton-loader`}
          isLoaded={!isRegistrantsLoading}
          width="100%"
        >
          {noOfRegistrants <= 0 ? (
            <EmptySectionCard
              caption={
                searchTerm.trim()
                  ? 'No registrants found with the provided search value'
                  : 'No registrants to display'
              }
              id={`${TOP_LEVEL_ID}__empty-section`}
            />
          ) : (
            <ChakraFlex flexDirection="column" gap="12px">
              {renderRegistrants}
            </ChakraFlex>
          )}
        </SkeletonLoader>
      </ChakraFlex>
    </ModalV2>
  );
};

export default ViewRegistrantsModal;
